import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { switchMap } from "rxjs/operators";
import { MOBILE_BASE_PATH } from "../../shell/mobile-routes.constants";
import { MobileModuleAccessService } from "../../profile/services/mobile-module-access.service";
import { MobileVoicePurchaseRequestRecorderComponent } from "../../voice-purchase-request/mobile-voice-purchase-request-recorder/mobile-voice-purchase-request-recorder.component";
import { MobileVoiceTicketService } from "../services/mobile-voice-ticket.service";

@Component({
  selector: "mobile-eachat-voice-demand-page",
  standalone: true,
  imports: [CommonModule, TranslateModule, MobileVoicePurchaseRequestRecorderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./mobile-eachat-voice-demand-page.component.html",
  styleUrl: "./mobile-eachat-voice-demand-page.component.scss",
})
export class MobileEachatVoiceDemandPageComponent {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleAccess = inject(MobileModuleAccessService);
  private readonly voiceTicketService = inject(MobileVoiceTicketService);

  protected readonly ticketAccess = this.moduleAccess.eachatTicketAccess;
  protected readonly errorMessageKey = signal<string | null>(null);
  protected readonly isSubmitting = signal(false);

  protected onSent(rawText: string): void {
    const text = rawText.trim();
    if (!text || this.isSubmitting() || !this.ticketAccess().hasCreateAccess) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    this.voiceTicketService
      .submitTranscript(text)
      .pipe(
        switchMap((demand) => this.voiceTicketService.generateDraft(demand.Uid)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.isSubmitting.set(false);
          void this.router.navigate([MOBILE_BASE_PATH, "eachat", "demandes", result.TicketUid]);
        },
        error: (err: Error) => {
          this.isSubmitting.set(false);
          this.errorMessageKey.set(this.resolveErrorKey(err.message));
        },
      });
  }

  protected onRecordError(key: string): void {
    this.errorMessageKey.set(key);
  }

  protected backToList(): void {
    void this.router.navigate([MOBILE_BASE_PATH, "eachat", "demandes"]);
  }

  protected clearError(): void {
    this.errorMessageKey.set(null);
  }

  protected errorMessage(): string {
    const key = this.errorMessageKey();
    return key ? this.translate.instant(key) : "";
  }

  private resolveErrorKey(message: string | undefined): string {
    const msg = message ?? "";
    if (msg.includes("ai.notconfigured") || msg.includes("voiceticket.ai.notconfigured")) {
      return "mobile.eachat.voice.error.aiNotConfigured";
    }
    if (msg.includes("voiceticket.catalog.loadfailed") || msg.includes("voiceticket.catalog.empty")) {
      return "mobile.eachat.voice.error.catalogEmpty";
    }
    if (msg.includes("voiceticket.ai.invalidjson") || msg.includes("voiceticket.ai.emptyresponse")) {
      return "mobile.eachat.voice.error.aiInvalidJson";
    }
    if (msg.includes("voiceticket.product.createfailed")) {
      return "mobile.eachat.voice.error.productCreateFailed";
    }
    if (msg.startsWith("mobile.")) {
      return msg;
    }
    return "mobile.eachat.voice.error.generic";
  }
}
